import { DeFiOpportunities } from "../../rest/kairos/types";
import { AssetId } from "../../config/assets";

export interface CompoundOpportunityConfig {
  /** Matches `id` from GET /kyros/defi-v2 */
  defiId: string;
  destinationTracking: string;
  title: string;
  protocol: string;
  strategyType: string;
  tokenIcons: readonly string[];
  badge?: string;
  fallbackDestination: string;
}

/** Kamino kySOL / JitoSOL LP — `id`: kamino-lp-jitosol */
export const KAMINO_KYSOL_JITOSOL_LP: CompoundOpportunityConfig = {
  defiId: "kamino-lp-jitosol",
  destinationTracking: "kamino-kysol-jitosol-lp",
  title: "kySOL / JitoSOL",
  protocol: "Kamino",
  strategyType: "Liquidity Provision",
  tokenIcons: ["/kysol.svg", "/jitosol.png"] as const,
  badge: "JTO Boosted",
  fallbackDestination:
    "https://kamino.com/liquidity/5ESAmxob1RsTrMVt7oaqvyDVoo4eD1Ns7s2xWiK4RjMK",
};

/** Kamino kySOL lending — `id`: kamino-lending */
export const KAMINO_KYSOL_LENDING: CompoundOpportunityConfig = {
  defiId: "kamino-lending",
  destinationTracking: "kamino-kysol-lending",
  title: "kySOL Lending",
  protocol: "Kamino",
  strategyType: "Lending",
  tokenIcons: ["/kysol.svg"] as const,
  fallbackDestination:
    "https://kamino.com/borrow/reserve/H6rHXmXoCQvq8Ue81MqNh7ow5ysPa1dSozwW3PU1dDH6/4pDCUGYftuPG1sV3xcqsn2YGfHC6Q3eZVfDyzQm8SK5A",
};

/** Kamino kyJTO / JTO LP — `id`: kamino-lp-kyjto-v2 */
export const KAMINO_KYJTO_JTO_LP: CompoundOpportunityConfig = {
  defiId: "kamino-lp-kyjto-v2",
  destinationTracking: "kamino-kyjto-jto-lp",
  title: "kyJTO / JTO",
  protocol: "Kamino",
  strategyType: "Liquidity Provision",
  tokenIcons: ["/kyjto.svg", "/jto.svg"] as const,
  badge: "JTO Boosted",
  fallbackDestination:
    "https://kamino.com/liquidity/C3X6B9XZnzZUrBK2MisUCdCjzX5Q4im3dwbetxpQkBtM",
};

export const SOL_COMPOUND_STRATEGIES: readonly CompoundOpportunityConfig[] = [
  KAMINO_KYSOL_JITOSOL_LP,
  KAMINO_KYSOL_LENDING,
];

export const JTO_COMPOUND_STRATEGIES: readonly CompoundOpportunityConfig[] = [
  KAMINO_KYJTO_JTO_LP,
];

export const COMPOUND_STRATEGIES_BY_ASSET: Partial<
  Record<AssetId, readonly CompoundOpportunityConfig[]>
> = {
  sol: SOL_COMPOUND_STRATEGIES,
  jto: JTO_COMPOUND_STRATEGIES,
};

export function findDeFiOpportunityById(
  opportunities: DeFiOpportunities[],
  defiId: string
): DeFiOpportunities | undefined {
  return opportunities.find((o) => o.id === defiId);
}

function parseDeFiNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function pickPoolApy(opportunity: DeFiOpportunities | undefined): number | null {
  if (!opportunity?.apy?.length) return null;
  const lp = opportunity.apy.find((a) => a.name?.toLowerCase() === "lp");
  if (lp != null && Number.isFinite(lp.apy)) return lp.apy;
  const primary = opportunity.apy.find((a) => a.apy > 0);
  return primary?.apy ?? opportunity.apy[0]?.apy ?? null;
}

export function resolveCompoundPoolMetrics(
  opportunity: DeFiOpportunities | undefined,
  defiLoading: boolean
) {
  const tvl = parseDeFiNumber(opportunity?.tvl);
  const apy = pickPoolApy(opportunity);

  return {
    tvl,
    apy,
    tvlLoading: defiLoading && tvl == null,
    apyLoading: defiLoading && apy == null,
  };
}
